import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  LinearProgress,
  useTheme,
  useMediaQuery,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import { AccountCircle, Logout } from '@mui/icons-material';
import { HeaderProps } from '../../types';

import { useAuth } from '../../hooks/useAuth';
import { LanguageSwitcher } from '../common/LanguageSwitcher';

export const Header: React.FC<HeaderProps> = ({ progress }) => {
  const { t } = useTranslation(['common', 'landing']);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  
  const { authState, logout } = useAuth();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const isAuthPage = location.pathname === '/auth' || location.pathname === '/login' || location.pathname.startsWith('/auth/');
  const isOnboardingPage = location.pathname.startsWith('/plan-selection') || location.pathname.startsWith('/payment') || location.pathname.startsWith('/subscription-required') || location.pathname.startsWith('/welcome-subscription');

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleMenuClose();
    try {
      await logout();
      window.location.href = '/auth';
    } catch (e) {
      console.error(e);
    }
  };

  const handleLogin = async () => {
    try {
      if (authState.isAuthenticated) {
        await logout();
      }
    } catch (e) {
      // Ignora falhas no logout para não bloquear a navegação
    }
    navigate('/auth');
  };

  const handleGetStarted = () => {
    navigate('/auth?signup=true');
  };

  const handleNavClick = (sectionId: string) => {
    if (location.pathname === '/') {
      const element = document.getElementById(sectionId);
      if (element) {
        const headerOffset = 70;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    } else {
      navigate(`/#${sectionId}`);
    }
  };

  const getUserInitials = () => {
    if (authState.user?.full_name) {
      return authState.user.full_name
        .split(' ')
        .map(name => name.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return authState.user?.username?.charAt(0).toUpperCase() || 'U';
  };

  if (authState.isAuthenticated && !isOnboardingPage) {
    return null;
  }

  return (
    <>
      <AppBar 
        position="fixed" 
        elevation={0} 
        sx={{ 
          backgroundColor: '#FFFFFF', 
          borderBottom: '1px solid #E1E8E7',
          boxShadow: '0px 1px 1px rgba(0,0,0,0.05)'
        }}
      >
        <Toolbar 
          sx={{ 
            display: 'flex',
            justifyContent: 'space-between',
            py: { xs: 1, md: 1.5 }, 
            minHeight: { xs: '60px', md: '70px' },
            px: { xs: 2, md: 4 },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Box 
              component={RouterLink} 
              to="/"
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                textDecoration: 'none',
                cursor: 'pointer',
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"New Order", sans-serif',
                  fontWeight: 700,
                  fontSize: { xs: '16px', md: '20px' },
                  color: '#003D4D',
                  letterSpacing: '-0.5px',
                  whiteSpace: 'nowrap',
                }}
              >
                MUU Agrotech
              </Typography>
            </Box>

            {!isMobile && !isAuthPage && !isOnboardingPage && (
              <>
                <Typography
                  onClick={() => handleNavClick('features')}
                  sx={{
                    fontFamily: '"New Order", sans-serif',
                    fontSize: '16px',
                    color: '#333B3E',
                    cursor: 'pointer',
                    '&:hover': {
                      color: '#003D4D',
                    },
                  }}
                >
                  {t('landing:header.product')}
                </Typography>
                <Typography
                  onClick={() => handleNavClick('solutions')}
                  sx={{
                    fontFamily: '"New Order", sans-serif',
                    fontSize: '16px',
                    color: '#333B3E',
                    cursor: 'pointer',
                    '&:hover': {
                      color: '#003D4D',
                    },
                  }}
                >
                  {t('landing:header.solutions')}
                </Typography>
                <Typography
                  onClick={() => handleNavClick('pricing')}
                  sx={{
                    fontFamily: '"New Order", sans-serif',
                    fontSize: '16px',
                    color: '#333B3E',
                    cursor: 'pointer',
                    '&:hover': {
                      color: '#003D4D',
                    },
                  }}
                >
                  {t('landing:header.pricing')}
                </Typography>
              </>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, md: 3 } }}>
            {!isAuthPage && !authState.isAuthenticated && (
              <Box 
                sx={{ 
                  display: 'flex', 
                  gap: { xs: 1, md: 2 }, 
                  alignItems: 'center',
                  flexShrink: 0,
                }}
              >
                <Box
                  onClick={handleLogin}
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontWeight: 600,
                    fontSize: '16px',
                    color: '#003D4D',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: { xs: 1.5, md: 2 },
                    py: { xs: 0.5, md: 0.5 },
                  }}
                >
                  {t('landing:header.login')}
                </Box>
                <Box
                  onClick={handleGetStarted}
                  sx={{
                    fontFamily: '"New Order", sans-serif',
                    fontWeight: 600,
                    fontSize: '16px',
                    color: '#FFFFFF',
                    backgroundColor: '#003D4D',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: { xs: 2, md: 3 },
                    py: { xs: 0.75, md: 1 },
                    borderRadius: '8px',
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                      backgroundColor: '#002A35',
                    },
                  }}
                >
                  {t('landing:header.get_started')}
                </Box>
              </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <LanguageSwitcher variant="compact" />

              {authState.isAuthenticated && isOnboardingPage && (
                <>
                  <IconButton
                    size="large"
                    onClick={handleMenuOpen}
                    sx={{ ml: 1 }}
                  >
                    <Avatar 
                      sx={{ 
                        width: 32, 
                        height: 32, 
                        bgcolor: 'primary.main',
                        fontSize: '0.875rem'
                      }}
                    >
                      {getUserInitials()}
                    </Avatar>
                  </IconButton>

                  <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleMenuClose}
                    onClick={handleMenuClose}
                    PaperProps={{
                      elevation: 3,
                      sx: {
                        overflow: 'visible',
                        filter: `drop-shadow(0px 2px 8px rgba(0, 0, 0, 0.15))`,
                        mt: 1.5,
                        minWidth: 200,
                        '& .MuiAvatar-root': {
                          width: 32,
                          height: 32,
                          ml: -0.5,
                          mr: 1,
                        },
                      },
                    }}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  >
                    <MenuItem disabled>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        {getUserInitials()}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2">
                          {authState.user?.full_name || authState.user?.username}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {authState.user?.email}
                        </Typography>
                      </Box>
                    </MenuItem>
                    
                    <Divider />
                    
                    <MenuItem onClick={handleLogout}>
                      <ListItemIcon>
                        <Logout fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>{t('navigation.logout')}</ListItemText>
                    </MenuItem>
                  </Menu>
                </>
              )}
            </Box>
          </Box>
        </Toolbar>
        {progress !== undefined && progress > 0 && progress < 100 && (
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            sx={{ 
              height: 2,
              backgroundColor: '#E2E8F0',
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#6BC43C'
              }
            }}
          />
        )}
      </AppBar>
    </>
  );
};